'use client';

import type { FC, HTMLAttributes, ReactNode, RefObject } from 'react';
import type { FilePreviewData } from '../FilePreview';
import type { RubricFieldType } from '@/types/annotations/types';
import { ChevronDown, ChevronUp, Loader2, MessageCircle, MessageCirclePlus, Pencil, RemoveFormatting } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button, Input, TextArea } from '@/components/era-ui';
import { useTipTapEditor } from '@/hooks/annotations/useTipTapEditor';
import { cn } from '@/libs/tailwind';
import { trpc } from '@/libs/trpc';
import { useCoyoteFileUploadStore } from '../../../state/annotations/use-coyote-file-upload-store';
import { FilePreview } from '../FilePreview';
import { FileThumbnail } from '../FileThumbnail';
import { ArrowIcon, SystemStarIcon } from '../icons/index';
import { TipTapActionBar, TipTapTextEditor } from '../tiptap-texteditor';

export const TaskContainer: FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={cn('relative w-full max-w-[767px] pointer-events-auto', className)}>
    {children}
  </div>
);

export const TaskBlockCard: FC<{
  children: ReactNode;
  ref?: RefObject<HTMLDivElement>;
  className?: string;
} & HTMLAttributes<HTMLDivElement>> = ({ children, ref, className, ...props }) => {
  return (
    <div
      ref={ref}
      className={cn('p-5 rounded-[16px] border border-[#D4D6EC] shadow-[0_0_12px_rgba(0,0,0,0.08)] flex flex-col gap-4 bg-white', className)}
      {...props}
    >
      {children}
    </div>
  );
};

export const TaskBlockCardWithNext: React.FC<{
  children: React.ReactNode;
  // TODO (achaturvedi): Combine button props into a single type
  onNextButtonClick?: () => Promise<void>; // TODO (achaturvedi): this should not expect a promise
  isNextButtonLoading?: boolean;
  isNextButtonDisabled?: boolean;
  fileUploadButton?: React.ReactNode; // Existing prop for file upload button
  onFileUpload?: (file: File) => void; // New prop for file upload functionality
  files?: { id: string; file: File; url: string }[]; // New prop for uploaded files
  onFileRemove?: (fileId: string) => void; // New prop for removing uploaded files
  isFileUploadLoading?: boolean; // New prop for file upload loading state
  // Use continue button (instead of submit arrow) when the next action doesn't submit any data
  continueButton?: boolean;
  showTipTapActionBar?: boolean; // New prop to control TipTapActionBar visibility
  enableFileUpload?: boolean; // New prop to control file upload functionality
  canExpandToFullHeight?: boolean; // New prop to control if block can expand to full height
  allowNextClickToValidate?: boolean; // New prop to allow clicks on (soft) disabled button for validation
}> = ({
  children,
  onNextButtonClick,
  isNextButtonLoading,
  isNextButtonDisabled,
  fileUploadButton,
  onFileUpload,
  files = [],
  onFileRemove,
  isFileUploadLoading = false,
  continueButton,
  showTipTapActionBar = false,
  enableFileUpload = false,
  canExpandToFullHeight = false,
  allowNextClickToValidate = false,
}) => {
  const { isCoyoteFileUploadEnabled } = useCoyoteFileUploadStore();
  const { isTipTapEnabled } = useTipTapEditor();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isActive, setIsActive] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [hoveringFileId, setHoveringFileId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FilePreviewData | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [label, setLabel] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Finds the first label's text content to display when the block is collapsed
  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    const extractFirstLine = (text: string | null | undefined): string | undefined => {
      return text?.trim()?.split('\n')[0]?.trim();
    };

    const getFirstLineOfText = (element: Element): string | undefined => {
      // Try label first
      const labelText = extractFirstLine(element.querySelector('label')?.textContent);
      if (labelText) {
        return labelText;
      }

      // Otherwise walk the tree
      const walkNode = (node: Node): string | undefined => {
        if (node.nodeType === Node.TEXT_NODE) {
          const firstLine = extractFirstLine(node.textContent);
          if (firstLine) {
            return firstLine;
          }
        }

        const children = Array.from(node.childNodes);
        for (const child of children) {
          const result = walkNode(child);
          if (result) {
            return result;
          }
        }
        return undefined;
      };

      return walkNode(element);
    };

    const firstLine = getFirstLineOfText(contentRef.current);
    if (firstLine) {
      setLabel(firstLine);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      // Only trigger if Meta+Enter (Cmd+Enter on Mac) is pressed
      if (
        keyboardEvent.key === 'Enter'
        && keyboardEvent.metaKey
        && !keyboardEvent.shiftKey
        && !keyboardEvent.ctrlKey
        && !keyboardEvent.altKey
        && onNextButtonClick
        && !isNextButtonLoading
        && (allowNextClickToValidate || !isNextButtonDisabled)
        && isActive
        && document.activeElement !== buttonRef.current // Prevent double submission when button is focused
      ) {
        keyboardEvent.preventDefault();
        onNextButtonClick();
      }
    };

    if (onNextButtonClick) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNextButtonClick, isNextButtonLoading, isNextButtonDisabled, isActive, allowNextClickToValidate]);

  const handleFileUpload = () => {
    if (!onFileUpload) {
      return;
    }

    // Create an input element to trigger file selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg,application/pdf';
    input.style.display = 'none';
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file) {
          // Let FileUploadButton or parent component handle validation
          onFileUpload(file);
        }
      }
    };
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleOpenPreview = (file: { id: string; file: File; url: string }) => {
    // Allow preview for both images and PDFs
    if (file.file.type.startsWith('image/') || file.file.type === 'application/pdf') {
      setPreviewFile({
        ...file,
        name: file.file.name,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  const isFileUploadEnabled = isCoyoteFileUploadEnabled || enableFileUpload;

  return (
    <>
      <TaskBlockCard
        ref={cardRef}
        className="w-full relative"
        tabIndex={0}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
      >
        {showTipTapActionBar && !isCollapsed && isTipTapEnabled && <TipTapActionBar />}

        <Button
          variant="tertiary"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            'focus-visible:ring-offset-1 bg-transparent rounded-[8px] border-none p-2 absolute right-[12px] w-[31px] h-[31px]',
            isCollapsed ? 'top-[50%] -translate-y-1/2' : 'top-[8px]',
          )}
        >
          {isCollapsed ? <ChevronUp /> : <ChevronDown />}
        </Button>

        {/* Always show label when collapsed */}
        {isCollapsed && label && (
          <div className="text-body-md font-medium max-w-[90%]">{label}</div>
        )}

        {/* Content container */}
        <div
          ref={contentRef}
          className={cn(
            'flex flex-col gap-4 transition-all duration-200',
            isCollapsed && 'hidden',
          )}
        >
          {/* Wrap children in a scrollable container */}
          <div className={cn(
            'flex flex-col gap-4 overflow-y-auto',
            canExpandToFullHeight ? 'max-h-[70vh]' : 'max-h-[40vh]',
          )}
          >
            {children}
          </div>

          {/* Display uploaded files if there are any */}
          {files.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3 justify-start">
              {files.map(file => (
                <FileThumbnail
                  key={file.id}
                  id={file.id}
                  file={file.file}
                  url={file.url}
                  isHovering={hoveringFileId === file.id}
                  onRemove={onFileRemove}
                  onClick={() => handleOpenPreview(file)}
                  onHoverChange={(id, isHovering) => setHoveringFileId(isHovering ? id : null)}
                />
              ))}
            </div>
          )}

          {(onNextButtonClick || fileUploadButton || (onFileUpload && isFileUploadEnabled)) && (
            <div className="flex justify-end items-center gap-2">
              {fileUploadButton}

              {/* Display the built-in upload button if onFileUpload is provided and feature flag is enabled */}
              {onFileUpload && isFileUploadEnabled && !fileUploadButton && (
                <Button
                  variant="secondary"
                  onClick={handleFileUpload}
                  className="group/upload-button focus-visible:ring-offset-1"
                  aria-label="Upload File"
                  disabled={isFileUploadLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-colors">
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                  <span>Upload</span>
                </Button>
              )}

              {onNextButtonClick && (
                continueButton ? (
                  <Button
                    variant="primary"
                    ref={buttonRef}
                    onClick={onNextButtonClick}
                    disabled={!allowNextClickToValidate && (isNextButtonDisabled || isNextButtonLoading)}
                    className={cn(
                      allowNextClickToValidate && isNextButtonDisabled && 'hover:bg-interactive-primary active:bg-interactive-primary cursor-pointer',
                      isNextButtonLoading && 'cursor-not-allowed',
                    )}
                    aria-label="Continue"
                  >
                    {isNextButtonLoading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      'Continue'
                    )}
                  </Button>
                ) : (
                  <Button
                    ref={buttonRef}
                    type="submit"
                    variant="icon"
                    size="icon"
                    onClick={onNextButtonClick}
                    disabled={!allowNextClickToValidate && (isNextButtonDisabled || isNextButtonLoading)}
                    className={cn(
                      'disabled:opacity-100 group/next-button focus-visible:ring-offset-1',
                      allowNextClickToValidate && isNextButtonDisabled && 'bg-white border-gray-200 text-fg-primary hover:bg-white hover:border-gray-200 cursor-pointer',
                      isNextButtonLoading && 'cursor-not-allowed',
                    )}
                    aria-label="Submit"
                  >
                    {isNextButtonLoading
                      ? <Loader2 className="size-6 animate-spin" />
                      : (
                          <ArrowIcon
                            direction="up"
                            className={cn(
                              'transition-colors',
                              allowNextClickToValidate && isNextButtonDisabled
                                ? 'text-fg-primary'
                                : 'text-white group-disabled/next-button:text-black',
                            )}
                          />
                        )}
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </TaskBlockCard>

      {/* Use the FilePreview component */}
      <FilePreview
        file={previewFile}
        onClose={handleClosePreview}
      />
    </>
  );
};

export const TaskBlockInstructionCard: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <div className="px-4 flex flex-row items-start gap-2">
      <SystemStarIcon />
      <div className="flex flex-col gap-2 text-body-md mt-[2px]">
        {children}
      </div>
    </div>
  );
};

export const TaskBlockReadOnlyCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div className="flex justify-end w-full">
      <div className={cn('space-y-4 p-4 rounded-[12px] bg-gray-100 w-full', className)}>
        {children}
      </div>
    </div>
  );
};

export const TaskBlockResponseCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div className="flex justify-end">
      <div className={
        cn(
          'space-y-4 justify-self-end max-w-[504px] min-h-11 py-2.5 px-5 rounded-[16px] bg-[#F1F1F1] text-body-md font-normal leading-[150%] text-wrap break-words',
          className,
        )
      }
      >
        {children}
      </div>
    </div>
  );
};

export const TaskBlockTextArea: React.FC<{
  value: string;
  onValueChange: (newValue: string) => void;
  className?: string;
  useTipTap?: boolean;
  fieldType?: RubricFieldType;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  value,
  onValueChange,
  className,
  onKeyDown,
  useTipTap = false,
  fieldType = 'paragraph',
  ...props
}) => {
  const { token, setToken, isTipTapEnabled } = useTipTapEditor();
  const { data } = trpc.auth.generateTipTapToken.useQuery(
    { currentToken: token ?? undefined },
  );

  useEffect(() => {
    if (data?.token) {
      setToken(data.token);
    }
  }, [data, setToken]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Only submit form on Meta+Enter (Cmd+Enter on Mac)
    if (e.key === 'Enter' && e.metaKey && !e.shiftKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      // Find the closest form and submit it
      const form = textareaRef.current?.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }

    // Call the original onKeyDown if provided
    onKeyDown?.(e);
  };

  useEffect(() => {
    // Auto-adjust height when the component mounts or value changes
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(36, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);

  // Return TipTap text editor if useTipTap is true, token is available, and feature flag is enabled
  if (useTipTap && token && isTipTapEnabled) {
    return <TipTapTextEditor value={value} onValueChange={onValueChange} className={className} onKeyDown={onKeyDown} token={token} placeholder={props.placeholder} {...props} />;
  }
  if (fieldType === 'short answer') {
    return (
      <Input
        value={value}
        className={className}
        onChange={e => onValueChange(e.target.value)}
        readOnly={false}
      />
    );
  }

  return fieldType === 'paragraph' && (
    <TextArea
      value={value}
      className={cn('text-body-md resize-none w-full min-h-9 h-auto', className)}
      onKeyDown={handleKeyDown}
      rows={2}
      ref={textareaRef}
      {...props}
      onChange={onValueChange} // override the onChange from the TextArea component because we use string instead of event
    />
  );
};

export const TaskBlockOptionButton: React.FC<{
  children: React.ReactNode;
  selected?: boolean;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  className,
  selected,
  disabled,
  ...props
}) => (
  <button
    type="button"
    className={cn(
      'flex items-center w-fit justify-start text-body-md text-left px-4 py-2.5 rounded-[8px] border border-[#D4D6EC] bg-white hover:bg-blue-15 hover:border-transparent transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-blue-20 cursor-pointer disabled:cursor-default disabled:hover:bg-white',
      selected && 'bg-[#BFD2FF] border-transparent disabled:hover:bg-[#BFD2FF]',
      className,
    )}
    disabled={disabled}
    {...props}
  >
    {children}
  </button>
);

export const TaskActionPill: React.FC<{
  onComment?: {
    onClick: () => void;
    className?: string;
  };
  onEdit?: {
    onClick: () => void;
    loading?: boolean;
    nondestructive?: boolean;
  };
  getComment?: () => void;
  markdown?: {
    toggleMarkdown: () => void;
    isMarkdown: boolean;
  };
  className?: string;
}> = ({
  onComment,
  onEdit,
  getComment,
  markdown,
  className,
}) => {
  return (
    <div className={cn('absolute -right-12 top-0', className)}>
      <div className="flex flex-col gap-2">
        {onComment && (
          <button
            type="button"
            className={cn(
              'p-2.5 rounded-[12px] ring-1 ring-neutral-20 shadow-2xl shadow-neutral-400 bg-white text-black hover:bg-neutral-10 hover:text-neutral-60 hover:cursor-pointer invisible group-hover-always:visible',
              onComment.className,
            )}
            onClick={onComment.onClick}
          >
            <MessageCirclePlus size={18} />
          </button>
        )}
        {onEdit && (
          <EditButtonWithConfirmation
            onClick={onEdit.onClick}
            loading={onEdit.loading}
            nondestructive={onEdit.nondestructive}
          />
        )}
        {getComment && (
          <button
            type="button"
            onClick={getComment}
            className="p-2.5 rounded-[12px] ring-1 ring-neutral-20 shadow-2xl shadow-neutral-400 bg-white text-black hover:bg-neutral-10 hover:text-neutral-60 hover:cursor-pointer visible group-hover-always:visible"
          >
            <MessageCircle size={18} />
          </button>
        )}
        {markdown && (
          <button
            type="button"
            onClick={markdown.toggleMarkdown}
            className={cn(
              'p-2.5 rounded-[12px] ring-1 ring-neutral-20 shadow-2xl shadow-neutral-400 bg-white text-black hover:bg-neutral-10 hover:text-neutral-60 hover:cursor-pointer invisible group-hover-always:visible',
              !markdown.isMarkdown && 'bg-black hover:bg-neutral-80 visible',
            )}
            title="Toggle Formatting"
          >
            <RemoveFormatting size={18} className={!markdown.isMarkdown ? 'text-white' : ''} />
          </button>
        )}
      </div>
    </div>
  );
};

const EditButtonWithConfirmation: React.FC<{
  onClick: () => void;
  loading?: boolean;
  nondestructive?: boolean;
}> = ({ onClick, loading }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-2.5 rounded-[12px] ring-1 ring-neutral-20 shadow-2xl shadow-neutral-400 bg-white text-black hover:bg-neutral-10 hover:text-neutral-60 hover:cursor-pointer invisible group-hover-always:visible"
      title="Edit this step"
      disabled={loading}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : <Pencil size={18} />}
    </button>
  );
};
